import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UploadCloud, FileText, Trash2, CheckCircle2, XCircle, Clock3, Eye, Download } from "lucide-react";

const CATEGORIES = ["CDL", "Insurance", "IRP", "IFTA", "Vehicle Registration", "Other"];

export default function Documents({ adminClientId }) {
  const { user } = useAuth();
  const isAdminView = !!adminClientId;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("CDL");
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = isAdminView ? `/documents?client_id=${adminClientId}` : "/documents";
      const { data } = await api.get(url);
      setDocs(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [adminClientId]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    if (isAdminView) fd.append("client_id", adminClientId);
    setUploading(true);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      window.dispatchEvent(new Event("hs:limits:refresh"));
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed";
      if (e?.response?.status === 402) {
        toast.error(detail, { description: "Please contact your compliance team.", duration: 6000 });
      } else {
        toast.error(detail);
      }
    } finally { setUploading(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";
  };

  const review = async (id, status) => {
    const notes = status === "rejected"
      ? (window.prompt("Rejection note (visible to client):") || "")
      : (window.prompt("Approval note (optional):") || "");
    try {
      await api.put(`/documents/${id}/review`, { status, notes });
      toast.success(`Document ${status}`);
      load();
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  const view = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot view sample document");
    }
  };

  const filtered = filter === "all" ? docs : docs.filter((d) => d.status === filter);

  return (
    <div className="space-y-6" data-testid="documents-page">
      {!isAdminView && (
        <div>
          <div className="hs-overline text-blue-700">DOCUMENT VAULT</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">Documents</h1>
          <p className="text-sm text-slate-500 mt-2">Upload CDLs, insurance, IRP cards, IFTA reports, registrations.</p>
        </div>
      )}

      {/* Upload */}
      {(user?.role !== "admin" || isAdminView) && (
        <div
          className={`hs-file-drop p-8 ${drag ? "drag" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); }}
          data-testid="document-drop-zone"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-14 h-14 bg-blue-700/10 border border-blue-700/40 flex items-center justify-center">
              <UploadCloud className="text-blue-700" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-lg">Drop file here or click to browse</div>
              <div className="text-xs text-slate-500 font-mono mt-1">PDF · PNG · JPG · WEBP · max 15MB</div>
              <div className="mt-4 flex flex-wrap gap-2 items-center">
                <label className="hs-overline">CATEGORY</label>
                <select
                  className="hs-input !w-auto !py-2"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="document-category-select"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input ref={inputRef} type="file" hidden onChange={onFile} accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="document-file-input" />
                <button onClick={() => inputRef.current?.click()} disabled={uploading} className="hs-btn-primary" data-testid="document-upload-button">
                  {uploading ? "UPLOADING…" : "BROWSE FILES"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {["all", "pending", "approved", "rejected"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`hs-btn-ghost text-xs ${filter === f ? "border border-blue-700 text-blue-700" : ""}`}
          >
            {f.toUpperCase()} ({f === "all" ? docs.length : docs.filter((d) => d.status === f).length})
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="hs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <Th>Document</Th>
              <Th>Category</Th>
              <Th>Status</Th>
              <Th>Uploaded</Th>
              <Th>Notes</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`document-row-${d.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="text-slate-500" />
                    <span className="truncate max-w-[260px]">{d.filename}</span>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{d.category}</td>
                <td className="px-4 py-3">
                  <StatusTag status={d.status} />
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{(d.uploaded_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate">{d.notes || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    {d.storage_path && (
                      <button onClick={() => view(d)} className="hs-btn-ghost p-2" title="View" data-testid={`document-view-${d.id}`}>
                        <Eye size={14}/>
                      </button>
                    )}
                    {user?.role === "admin" && (
                      <>
                        <button onClick={() => review(d.id, "approved")} className="hs-btn-ghost p-2 text-emerald-600" title="Approve" data-testid={`document-approve-${d.id}`}>
                          <CheckCircle2 size={14}/>
                        </button>
                        <button onClick={() => review(d.id, "rejected")} className="hs-btn-ghost p-2 text-red-600" title="Reject" data-testid={`document-reject-${d.id}`}>
                          <XCircle size={14}/>
                        </button>
                      </>
                    )}
                    <button onClick={() => remove(d.id)} className="hs-btn-ghost p-2 text-slate-500" title="Delete" data-testid={`document-delete-${d.id}`}>
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">No documents found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 hs-overline text-${align}`}>{children}</th>;
}

export function StatusTag({ status }) {
  const map = {
    approved: { tag: "hs-tag-success", icon: CheckCircle2, label: "APPROVED" },
    rejected: { tag: "hs-tag-danger", icon: XCircle, label: "REJECTED" },
    pending:  { tag: "hs-tag-warn", icon: Clock3, label: "PENDING" },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`hs-tag ${s.tag}`}>
      <Icon size={11} /> {s.label}
    </span>
  );
}
